import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, DollarSign } from 'lucide-react';

const RefundPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <h1 className="text-3xl md:text-4xl font-black text-[#d9fb06] uppercase mb-8">
            Refund Policy
          </h1>
          
          <div className="prose prose-invert max-w-none space-y-6 text-[#888680]">
            <p className="text-sm">Last updated: February 2025</p>
            
            {/* Partial Refund Policy - Highlighted */}
            <section>
              <div className="bg-gradient-to-r from-[#3f4816]/50 to-[#302f2c] border-2 border-[#d9fb06]/50 rounded-2xl p-6 md:p-8 my-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#d9fb06] flex items-center justify-center flex-shrink-0">
                    <Shield className="w-7 h-7 text-[#1a1c1b]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-[#d9fb06] uppercase mb-2">
                      $200 Denial Credit
                    </h2>
                    <p className="text-lg text-[#dfddd6] mb-4">
                      If the platform issues a final denial after our documentation process is complete, a partial refund of $200 may be available.
                      This is a limited refund policy, not an outcome guarantee.
                    </p>
                    <div className="bg-[#1a1c1b] rounded-xl p-4 mt-4">
                      <p className="text-sm text-[#888680]">
                        <strong className="text-[#dfddd6]">Eligibility:</strong> This applies when our documentation has been submitted to the platform 
                        and the platform has issued a final denial after our complete documentation process (including any follow-up materials if applicable). 
                        The $200 partial refund will be processed within 14 business days of confirmed denial.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">Service-Based Fees</h2>
              <p>
                Social Dispute Co charges for document preparation and submission services. 
                Our fees cover the professional time, expertise, and resources required to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Review your case details</li>
                <li>Prepare formal dispute documentation</li>
                <li>Compile supporting evidence</li>
                <li>Prepare documentation for submission through official appeal channels</li>
                <li>Track and monitor responses</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">Refund Eligibility</h2>
              
              <div className="bg-[#302f2c] p-6 rounded-xl border border-[#d9fb06]/30 my-4">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-5 h-5 text-[#d9fb06]" />
                  <p className="text-[#d9fb06] font-bold">$200 Partial Refund Eligibility:</p>
                </div>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Platform denies appeal after documentation submitted</li>
                  <li>Platform has issued final denial</li>
                  <li>All required documentation was provided by client</li>
                </ul>
              </div>
              
              <div className="bg-[#302f2c] p-6 rounded-xl border border-[#3f4816]/50 my-4">
                <p className="text-[#dfddd6] font-semibold mb-2">Full Refund Available:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>If we have not yet begun work on your case</li>
                  <li>Request made within 24 hours of payment and before case review</li>
                </ul>
              </div>
              
              <div className="bg-[#302f2c] p-6 rounded-xl border border-[#3f4816]/50 my-4">
                <p className="text-[#dfddd6] font-semibold mb-2">Partial Refund May Be Available:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>If case review has begun but documentation not yet submitted</li>
                  <li>Determined on a case-by-case basis</li>
                </ul>
              </div>
              
              <div className="bg-[#302f2c] p-6 rounded-xl border border-[#3f4816]/50 my-4">
                <p className="text-[#dfddd6] font-semibold mb-2">No Refund Available:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Client fails to provide requested information or documentation</li>
                  <li>Client provides false or misleading information</li>
                  <li>Account violation involved illegal activity</li>
                </ul>
              </div>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">How to Request a Refund</h2>
              <p>To request a refund, contact us with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your Case ID</li>
                <li>Your name and email address used for the order</li>
                <li>Date of payment</li>
                <li>Reason for refund request</li>
                <li>For $200 Denial Credit: Proof of platform&apos;s final denial</li>
              </ul>
              <p className="mt-4 text-[#dfddd6]">
                Email: intake@socialdisputeco.com<br />
                Phone: 808-301-2134
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-bold text-[#dfddd6] mt-8 mb-4">Processing Time</h2>
              <p>
                Approved refunds are typically processed within 5-14 business days. Refunds will 
                be issued to the original payment method when possible.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default RefundPolicyPage;
